import {
  COOKIE_NAME__EXPIRES_MAX_AGE__3M,
  COOKIE_NAME__PICKUP_LOCATION,
  getAddressLabels,
  getGeocodeReverseFn,
  getRecentLocationFn,
  updateRecentLocationFn,
  type Address,
  type AxiosError,
  type LngLat,
  type Path,
  type RecentLocation,
  type SavedPlace,
  type ShortestRoute,
} from '@ahm/api-wrappers-core';
import { getCookie, hasCookie, setCookie } from 'cookies-next';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { cleanPathObject, safeParseJSON } from '../lib/utils';
import { createSelectors } from './selectors';
import { vanillaMapStore } from './use-map-control';

// Helper function to convert RecentLocation to Address format
const convertRecentLocationToAddress = (
  recentLocation: RecentLocation
): Address => {
  const [firstLabel, secondLabel] = getAddressLabels(recentLocation.address);

  return {
    address: recentLocation.address,
    lat: recentLocation.location.coordinates[1], // latitude is second coordinate
    lng: recentLocation.location.coordinates[0], // longitude is first coordinate
    first_label: firstLabel,
    second_label: secondLabel,
    adr_source: recentLocation.adr_source || recentLocation._id,
  };
};

type LocationState = {
  location: LngLat | null;
  address: Address | null;
  error: Error | AxiosError | null;
  loading: boolean;
  paths: Path[];
  pathSortIndexes: number[] | null;
  selectedSavedPlace: SavedPlace | null;
  selectedPinMap: Address | null;
  activePath: Path | null;
  currentLocation: Address | null;
  frequentLocations: Address[];
  pathFocusIndex: number | null;
  shortestRoute: ShortestRoute | null;
  shortestRouteIndex: number[];
  savedLocations: Address[];
};

type LocationActions = {
  detectLocation: (force?: boolean) => Promise<Address>;
  setLocation: (location: LngLat) => void;
  setAddress: (address: Address) => void;
  setPaths: (paths: Path[]) => void;
  setPathSortIndexes: (pathSortIndexes: number[] | null) => void;
  updatePathByIndex: (path: Path, index: number) => void;
  updateAllPath: (path: Path[]) => void;
  removePath: (index: number) => void;
  clearPaths: () => void;
  setActivePath: (path?: Path) => void;
  loadFrequentLocations: () => Promise<void>;
  addToFrequentLocations: (location: Address) => Promise<void>;
  setSelectedSavedPlace: (savedPlace: SavedPlace | null) => void;
  setSelectedPinMap: (pinMap: Address | null) => void;
  setPathFocusIndex: (pathIndex: number | null) => void;
  setShortestRoute: (shortestRoute: ShortestRoute | null) => void;
  setShortestRouteIndex: (shortestRouteIndex: number[]) => void;
  setSavedLocations: (saved: Address[]) => void;
};

const store = create<
  LocationState & LocationActions,
  [['zustand/devtools', LocationState & LocationActions]]
>(
  // eslint-disable-next-line sonarjs/cognitive-complexity
  devtools((set, get) => {
    const pickupLocationCookie = hasCookie(COOKIE_NAME__PICKUP_LOCATION)
      ? getCookie(COOKIE_NAME__PICKUP_LOCATION)
      : undefined;

    const pickupPoint = pickupLocationCookie
      ? cleanPathObject<Path>(safeParseJSON<Path>(String(pickupLocationCookie)))
      : undefined;

    let paths: Path[] = [];

    if (pickupPoint) {
      paths = [pickupPoint];
    }

    try {
      const setCenterCoords = vanillaMapStore.getState().setCenterCoords;

      if (paths && paths.length > 0) {
        const pickupPoint: Path | null = paths ? paths[0] : null;
        if (pickupPoint?.lat && pickupPoint.lng) {
          setCenterCoords({
            latitude: pickupPoint.lat,
            longitude: pickupPoint.lng,
          });
        }
      } else {
        setCenterCoords(null);
      }
    } catch (error) {
      console.log('error', error);
    }

    const frequentLocations: Address[] = [];

    return {
      location: null,
      address: null,
      error: null,
      loading: false,
      paths,
      selectedSavedPlace: null,
      selectedPinMap: null,
      pathSortIndexes: null,
      frequentLocations,
      pathFocusIndex: null,
      shortestRoute: null,
      shortestRouteIndex: [],
      currentLocation: null,
      activePath: null,
      savedLocations: [],

      detectLocation: (force = false) => {
        return new Promise<Address>((resolve, reject) => {
          if (!(typeof navigator === 'object' && 'geolocation' in navigator)) {
            const error = new Error(
              'Phương thức hiện không được hỗ trợ, vui lòng thử lại sau.'
            );
            set({
              error,
              loading: false,
            });
            reject(error.message);
          } else {
            set({ loading: true, error: undefined });
            navigator.geolocation.getCurrentPosition(
              async (position: GeolocationPosition) => {
                const newLocation = {
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                } as LngLat;

                // if new location is the same as current location, do nothing
                const detectedLocation = get().location;
                const currentLocation = get().currentLocation;
                if (
                  !force &&
                  detectedLocation &&
                  currentLocation &&
                  detectedLocation.lat === newLocation.lat &&
                  detectedLocation.lng === newLocation.lng
                ) {
                  set({ loading: false });
                  resolve(currentLocation);
                  return;
                }

                try {
                  const address = await getGeocodeReverseFn(newLocation);

                  if (!address) {
                    const error = new Error(
                      'Không thể xác định được vị trí hiện tại, vui lòng thử lại sau.'
                    );
                    set({
                      error,
                      loading: false,
                    });

                    reject(error.message);
                    return;
                  }

                  set((state) => ({
                    location: newLocation,
                    currentLocation: address,
                    paths:
                      state.paths.length === 0
                        ? [
                            {
                              ...address,
                              name: '',
                              mobile: '',
                            } as Path,
                          ]
                        : [...state.paths],
                    loading: false,
                  }));
                  resolve(address);
                } catch (error) {
                  set({
                    location: newLocation,
                    error: error as AxiosError,
                    loading: false,
                  });
                  reject((error as Error).message);
                }
              },
              (error: GeolocationPositionError) => {
                set({
                  error: error as unknown as Error,
                  loading: false,
                });
                reject(error.message);
              },
              {
                enableHighAccuracy: true,
                maximumAge: 60 * 60 * 1000,
                timeout: 8000,
              }
            );
          }
        });
      },
      setLocation: (location) => {
        if (!location?.lat || !location.lng) {
          set({ location: undefined, address: undefined });
          return;
        }
        set({ location });
      },
      setAddress: (address) => {
        if (!address?.lat || !address.lng) {
          set({ location: undefined, address: undefined });
          return;
        }
        const currentLocation = get().location;
        // Only update location if new lat, lng from address are different from current location
        if (
          currentLocation &&
          currentLocation.lat === address.lat &&
          currentLocation.lng === address.lng
        ) {
          set({ address });
          return;
        }
        set({
          location: { lat: address.lat, lng: address.lng } as LngLat,
          address,
        });
      },
      setPaths: (paths) => {
        set({ paths });
      },
      setPathSortIndexes: (pathSortIndexes) => {
        set({ pathSortIndexes });
      },
      updatePathByIndex: (path, index) => {
        set((state) => {
          const paths = [...state.paths];
          if (index === -1 || index === -2) {
            if (paths.length === 0) {
              delete path.cod;
              delete path.tracking_number;
            }
            paths.push(path);
          } else {
            if (index === 0) {
              delete path.cod;
              delete path.tracking_number;
            }
            paths[index] = path;
          }
          return { paths };
        });
      },
      updateAllPath: (paths) => {
        set({ paths });
      },
      removePath: (index) => {
        set((state) => ({
          paths: state.paths.filter((_, i) => i !== index),
        }));
      },
      clearPaths: () => {
        set({ paths: [] });
      },
      setActivePath: (path) => {
        set({ activePath: path });
      },
      setPathFocusIndex: (pathIndex) => {
        set({ pathFocusIndex: pathIndex });
      },
      addToFrequentLocations: async (location: Address) => {
        try {
          await updateRecentLocationFn({
            location: {
              address: location.address,
              lat: location.lat!,
              lng: location.lng!,
            },
          });

          // Reload the list after successful update
          const recentLocations = await getRecentLocationFn();
          const addressList = recentLocations.map(
            convertRecentLocationToAddress
          );
          set({ frequentLocations: addressList });
        } catch (error) {
          console.error('Error adding frequent location', error);
        }
      },
      loadFrequentLocations: async () => {
        try {
          const recentLocations = await getRecentLocationFn();
          const addressList = recentLocations.map(
            convertRecentLocationToAddress
          );
          set({ frequentLocations: addressList });
        } catch (error) {
          console.error('Error loading frequent locations', error);
          set({ frequentLocations: [] });
        }
      },
      setSavedLocations(saved: Address[]) {
        set({ savedLocations: saved });
      },
      setSelectedSavedPlace: (savedPlace) => {
        set({ selectedSavedPlace: savedPlace });
      },
      setSelectedPinMap: (pinMap) => {
        set({ selectedPinMap: pinMap });
      },
      setShortestRoute: (shortestRoute: ShortestRoute | null) => {
        set({ shortestRoute });
      },
      setShortestRouteIndex: (shortestRouteIndex: number[]) => {
        set({ shortestRouteIndex });
      },
    };
  })
);

export const useLocationStore = createSelectors(store);

// Auto-load recent locations when store is initialized
void (async () => {
  try {
    await useLocationStore.getState().loadFrequentLocations();
  } catch (error) {
    // Ignore errors during auto-load - user might not be authenticated
    console.debug('Could not auto-load recent locations:', error);
  }
})();

useLocationStore.subscribe((state) => {
  const setCenterCoords = vanillaMapStore.getState().setCenterCoords;

  if (state.paths && state.paths.length > 0) {
    const pickup = cleanPathObject<Path>(state.paths[0]);
    void setCookie(COOKIE_NAME__PICKUP_LOCATION, JSON.stringify(pickup), {
      maxAge: COOKIE_NAME__EXPIRES_MAX_AGE__3M * 24 * 60 * 60,
    });

    const pickupPoint: Path | null = state.paths ? state.paths[0] : null;
    if (pickupPoint?.lat && pickupPoint.lng) {
      setCenterCoords({
        latitude: pickupPoint.lat,
        longitude: pickupPoint.lng,
      });
    }
  } else {
    setCenterCoords(null);
  }
});
